if (!customElements.get('store-locator')) {
  const escapeHtml = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char],
    );

  class StoreLocator extends HTMLElement {
    #rows = [];
    #sortKey = null;
    #sortDir = 1;
    #refs = {};
    #logoWidth = '90';

    connectedCallback() {
      this.#refs = {
        tbody: this.querySelector('[data-sl-body]'),
        search: this.querySelector('[data-sl-search]'),
        state: this.querySelector('[data-sl-state]'),
        empty: this.querySelector('[data-sl-empty]'),
        count: this.querySelector('[data-sl-count]'),
        headers: [...this.querySelectorAll('[data-sort-key]')],
      };
      this.#logoWidth = this.getAttribute('data-logo-width') ?? '90';

      this.#rows = this.#collect();
      this.#buildStateOptions();
      this.#bind();
      this.#render();
    }

    #collect() {
      return [...document.querySelectorAll('script[data-sl-data]')].flatMap((script) => {
        try {
          const parsed = JSON.parse(script.textContent?.trim() || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      });
    }

    #buildStateOptions() {
      const { state } = this.#refs;
      if (!state) return;

      const states = [...new Set(this.#rows.map((row) => row.state?.trim()).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b),
      );

      state.append(
        ...states.map((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          return option;
        }),
      );
    }

    #bind() {
      const { search, state, headers } = this.#refs;

      search?.addEventListener('input', () => this.#render());
      state?.addEventListener('change', () => this.#render());

      for (const header of headers) {
        const sort = () => this.#sortBy(header.dataset.sortKey);
        header.addEventListener('click', sort);
        header.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            sort();
          }
        });
      }
    }

    #sortBy(key) {
      if (this.#sortKey === key) {
        this.#sortDir *= -1;
      } else {
        this.#sortKey = key;
        this.#sortDir = 1;
      }
      this.#updateSortIndicators();
      this.#render();
    }

    #updateSortIndicators() {
      for (const header of this.#refs.headers) {
        const isActive = header.dataset.sortKey === this.#sortKey;
        header.classList.toggle('is-sorted-asc', isActive && this.#sortDir === 1);
        header.classList.toggle('is-sorted-desc', isActive && this.#sortDir === -1);
      }
    }

    #filtered() {
      const term = this.#refs.search?.value.trim().toLowerCase() ?? '';
      const stateValue = this.#refs.state?.value ?? '';

      const rows = this.#rows.filter((row) => {
        if (stateValue && (row.state ?? '') !== stateValue) return false;
        if (!term) return true;
        const haystack = `${row.name ?? ''} ${row.address ?? ''} ${row.state ?? ''}`.toLowerCase();
        return haystack.includes(term);
      });

      if (this.#sortKey) {
        rows.sort(
          (a, b) =>
            (a[this.#sortKey] ?? '')
              .toString()
              .localeCompare((b[this.#sortKey] ?? '').toString(), undefined, { sensitivity: 'base' }) *
            this.#sortDir,
        );
      }

      return rows;
    }

    #render() {
      const { tbody, empty, count } = this.#refs;
      if (!tbody) return;

      const rows = this.#filtered();

      tbody.innerHTML = rows
        .map((row) => {
          const logo = row.logo
            ? `<img src="${escapeHtml(row.logo)}" alt="${escapeHtml(row.name)}" loading="lazy" style="max-width:${this.#logoWidth}px">`
            : '<span class="sl_logo_placeholder"></span>';

          return `<tr>
            <td class="sl_logo_cell">${logo}</td>
            <td class="sl_cell_name">${escapeHtml(row.name)}</td>
            <td class="sl_cell_address">${escapeHtml(row.address)}</td>
            <td class="sl_cell_state">${escapeHtml(row.state)}</td>
          </tr>`;
        })
        .join('');

      empty?.classList.toggle('hidden', rows.length > 0);
      if (count) count.textContent = rows.length;
    }
  }

  customElements.define('store-locator', StoreLocator);
}
